<template>
  <div class="mt-2 variations-table">
    <v-data-table
      :headers="headers"
      :items="variationsList"
      item-key="id"
      :single-expand="true"
      show-expand
      :item-class="claimRowClass"
    >
      <template #[`item.no`]="{ item }">
        <v-chip :class="colorForStage[item.stage]">
          {{item.no}}
        </v-chip>
      </template>
      <template #[`item.lastUpdate`]="{ item }">
        <span class="nowrap">{{item.lastUpdateDate | formatDate}}</span>
      </template>

      <template #[`item.eot`]="{ item }">
        <div class="d-flex flex-column">
          <span v-if="item.eotRequested !== null">
            <v-icon
              class="primary--text"
              small
            >mdi-calendar-clock</v-icon> {{item.eotRequested }}
          </span>
          <span v-if="item.eotAgreed !== null">
            <v-icon
              class="success--text"
              small
            >mdi-timetable</v-icon> {{item.eotAgreed }}
          </span>

        </div>
      </template>

      <template #[`item.value`]="{ item }">
        <div class="d-flex flex-column">
          <small>
            <span class="nowrap" v-if="item.estimatedValueCents && item.estimatedValueCents.sum">
              <v-icon
                class="orange--text"
                small
              >mdi-approximately-equal</v-icon>
              {{item.estimatedValueCents.sum | formatCurrency({ key: item.currency, value: 1 }) }}
            </span>
          </small>
          <small>
            <span class="nowrap" v-if="item.originalValueCents && item.originalValueCents.sum">
              <v-icon
                class="primary--text"
                small
              >mdi-tag</v-icon>
              {{item.originalValueCents.sum | formatCurrency({ key: item.currency, value: 1 }) }}
            </span>
          </small>
          <small class="">
            <span class="nowrap" v-if="item.agreedValueCents && item.agreedValueCents.sum">
              <v-icon
                class="success--text"
                small
              >mdi-handshake</v-icon>
              {{item.agreedValueCents.sum |formatCurrency({ key: item.currency, value: 1 }) }}
            </span>
          </small>
        </div>
      </template>

      <template #[`item.description`]="{ item }">
        <div v-if="item.description" class="notes-cell">
          <span class="text--secondary">{{item.description}}</span>
        </div>
      </template>

      <template #[`item.part`]="{ item }">
        <div v-if="item.part">
          {{item.part.name}}
          <small class="d-block text--secondary">{{item.purchaseOrder}}</small>
        </div>
        <div v-else>
          {{ $t('part.all') }}
          <small class="d-block text--secondary">{{item.purchaseOrder}}</small>
        </div>
      </template>
      <template #[`item.status`]="{ item }">
        <div class="">
          <v-chip
            :color="statusesMap[item.status] ? statusesMap[item.status].color : ''"
            small
          >{{statusesMap[item.status] ? statusesMap[item.status].name : ''}}</v-chip>
        </div>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-btn
          icon
          small
          @click="handleEditVariationClick(item)"
          :disabled="item.isGhost || !canUserEdit"
        >
          <v-icon>mdi-pencil </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          @click="handleAddComment(item)"
          :disabled="item.isGhost || !canUserComment"
        >
          <v-icon>mdi-message-processing</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          @click="handleAddDocument(item)"
          :disabled="item.isGhost || !canUserComment"
        >
          <v-icon>mdi-text-box-plus-outline</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          @click="handleDelete(item)"
          :disabled="!item.canBeDeleted"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>

      </template>

      <template #[`expanded-item`]="{ headers, item }">
        <td
          class="white py-2 "
          :colspan="headers.length"
        >
          <v-row>
            <v-col cols="12" v-if="item.parts.length > 1">
              <VariationsPartsTable
                :parts="item.parts"
                :reporter="item.reportingSide"
                :isMultiCurrency="isMultiCurrency"
              />
            </v-col>
            <v-col cols="6" class="pl-0">
              <v-col cols="12">
                <v-card>
                  <v-card-text>
                    <small>{{ $t('common.description') }}</small>
                    <p>{{ item.description }}</p>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-card>
                  <v-card-text>
                    <small>{{ $tc('document.document', 2) }}</small>
                    <documents-table dense :documents="item.documents"/>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-col>
            <v-col class="pr-0" cols="6">
              <VariationTimeline
                :history="item.revisionHistory"
                @SAVE_HISTORY_COMMENT="$emit('SAVE_HISTORY_COMMENT', { variation: item, historyEntry: $event })"
                @DELETE_COMMENT="$emit('DELETE_COMMENT', item)"
              />
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { VARIATIONS_HEADERS } from '@/helpers'

export default {
  name: 'VariationsTable',
  props: {
    variations: {
      type: Array,
      default: () => []
    },
    type: String,
    chosenCurrency: Object,
    partsList: Array,
    isMultiCurrency: Boolean
  },
  data () {
    return {
      days: null,
      key: 0,
      colorForStage: ['', 'orange', 'primary'],
      headers: VARIATIONS_HEADERS[this.type].map(i => {
        return {
          ...i,
          text: i.key ? this.$t(`variation.headers.${i.key}`) : ''
        }
      })
    }
  },
  computed: {
    ...mapState({
      statusesMap: (state) => state.statuses.reduce((acc, curr) => ({ ...acc, [curr.code]: curr }), {}),
      canUserEdit: state => state.userPermissionsToContract.canUserEdit,
      canUserComment: state => state.userPermissionsToContract.canUserComment
    }),
    variationsList () {
      return this.variations.map(variation => {
        const revisionHistory = Array.isArray(variation.revisionHistory) ? variation.revisionHistory : []
        const parts = Array.isArray(variation.parts) ? variation.parts : []
        const partsList = Array.isArray(this.partsList) ? this.partsList : []
        const dates = revisionHistory.filter(i => i.type === 'update').map(i => i.createdAt).sort((a, b) => new Date(b) - new Date(a))
        const status = this.statusesMap[variation.status]
        const multiCurrency = [...new Set(parts.map(i => i.currency))].length > 1
        const defaultCurrency = this.chosenCurrency && this.chosenCurrency.key
          ? this.chosenCurrency.key
          : (parts[0] ? parts[0].currency : null)
        return {
          ...variation,
          revisionHistory,
          parts,
          lastUpdateDate: dates[0] || variation.receivedDate,
          isGhost: this.type === 'claim' && status && status.code === 'C2V',
          currency: parts.length === partsList.length || multiCurrency
            ? defaultCurrency
            : (parts[0] ? parts[0].currency : defaultCurrency)
        }
      })
    }
  },
  methods: {
    async handleDelete (item) {
      this.$emit('DELETE_VARIATION', item)
    },
    claimRowClass (item) {
      return item.isGhost ? 'ghost-row' : ''
    },
    handleEditVariationClick (item) {
      this.$emit('EDIT_VARIATION', item)
    },
    handleAddComment (item) {
      this.$emit('ADD_COMMENT_TO_VARIATION', item)
    },
    handleAddDocument (item) {
      this.$emit('ADD_DOCUMENT_TO_VARIATION', item)
    }
  }
}
</script>

<style>
.notes-cell {
  max-width: 250px;
  white-space: pre-wrap;
  word-break: break-word;
}
.ghost-row {
  opacity: 0.5;
}
.variations-table .v-data-table td {
  padding-right: 0 !important;
}

.variations-table .v-data-table th {
  padding-right: 0 !important;
  vertical-align: top !important;
}
</style>
